import { HelpOutlined as HelpOutline } from "@mui/icons-material";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import React from "react";
// Hover help guidelines: docs/guides/HOVER_HELP.md

import { adminUiCopy } from "@/copy/adminUiCopy";

interface InfoTooltipProps {
  title: ReactNode;
  description: ReactNode;
  /**
   * Accessible label for the help icon button.
   *
   * @defaultValue `"Help: ${title}"` when title is a string, otherwise `"More information"`.
   */
  ariaLabel?: string;
  size?: "small" | "medium" | "large";
  color?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  description,
  ariaLabel,
  size = "small",
  color,
}) => {
  const theme = useTheme();
  const resolvedAriaLabel =
    ariaLabel ??
    (typeof title === "string"
      ? `${adminUiCopy.common.helpAriaPrefix}: ${title}`
      : adminUiCopy.common.moreInformation);
  const iconColor = color ?? alpha(theme.palette.common.black, 0.54);

  return (
    <Tooltip
      title={
        <Box>
          <Box sx={{ fontWeight: "bold", mb: 1 }}>{title}</Box>
          <Box
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.4,
              "& ul": {
                paddingLeft: 2.5,
                marginTop: 0.5,
                marginBottom: 0.5,
              },
              "& li": {
                marginBottom: 0.5,
              },
            }}
          >
            {description}
          </Box>
        </Box>
      }
      placement="top"
      arrow
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: alpha(theme.palette.common.black, 0.9),
          color: "white",
          fontSize: "0.875rem",
          maxWidth: 300,
          padding: "12px 16px",
        },
        "& .MuiTooltip-arrow": {
          color: alpha(theme.palette.common.black, 0.9),
        },
      }}
    >
      <IconButton
        size={size}
        aria-label={resolvedAriaLabel}
        sx={{
          padding: "2px",
          color: iconColor,
          opacity: 0.7,
          "&:hover": {
            opacity: 1,
            backgroundColor: alpha(theme.palette.common.black, 0.04),
          },
        }}
      >
        <HelpOutline fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
};

export default InfoTooltip;
